import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, Loader2, CopyX, Mail, Phone } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DoublonsCandidats = ({ onChanged }) => {
  const [groupes, setGroupes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purging, setPurging] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/candidats/doublons`);
      setGroupes(res.data.groupes || []);
    } catch (e) {
      toast.error('Erreur lors de la recherche des doublons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const purgeables = groupes.reduce((n, g) => {
    const garder = g.some(c => c.dossiers.length) ? 0 : 1;
    return n + g.filter(c => !c.dossiers.length).length - garder;
  }, 0);

  const handleDelete = async (c) => {
    setDeleting(c.id);
    try {
      await axios.delete(`${API_URL}/candidats/${c.id}`);
      toast.success(`${c.nom} ${c.prenom} supprimé`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(null);
    }
  };

  const handlePurge = async () => {
    setPurging(true);
    try {
      const res = await axios.post(`${API_URL}/candidats/doublons/purger`);
      toast.success(`${res.data.supprimes} doublon(s) supprimé(s)`);
      setPurgeOpen(false);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la purge');
    } finally {
      setPurging(false);
    }
  };

  if (loading) {
    return <Card><CardContent className="p-6 space-y-3">{[1, 2].map(i => <div key={i} className="h-16 bg-slate-100 rounded animate-pulse" />)}</CardContent></Card>;
  }

  if (groupes.length === 0) {
    return (
      <Card className="border-dashed" data-testid="doublons-empty">
        <CardContent className="py-12 text-center">
          <CopyX className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-1">Aucun doublon</h3>
          <p className="text-slate-500">Aucun candidat ne partage le même NOM + Prénom ou le même email.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="doublons-view">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-slate-500">
          <span className="font-semibold text-slate-900">{groupes.length}</span> groupe(s) de doublons —{' '}
          <span className="font-semibold text-slate-900">{purgeables}</span> fiche(s) supprimable(s) (non affectées)
        </p>
        <Button variant="outline" className="border-red-200 text-red-700 hover:bg-red-50" disabled={purgeables === 0} onClick={() => setPurgeOpen(true)} data-testid="purge-doublons-btn">
          <Trash2 className="w-4 h-4 mr-2" />Supprimer tous les doublons non affectés
        </Button>
      </div>

      {groupes.map((g, gi) => (
        <Card key={gi} className="border-amber-200 shadow-sm overflow-hidden" data-testid={`doublon-groupe-${gi}`}>
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2">
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-200 text-amber-800 font-semibold uppercase tracking-wide">Doublon</span>
            <span className="text-sm font-medium text-slate-900">{g[0].nom} {g[0].prenom}</span>
            <span className="text-xs text-slate-500">· {g.length} fiches · {g.every(c => c.nom === g[0].nom && c.prenom === g[0].prenom) ? 'même NOM + Prénom' : 'même email'}</span>
          </div>
          <div className="divide-y">
            {g.map((c) => {
              const affecte = c.dossiers.length > 0;
              return (
                <div key={c.id} className="grid grid-cols-[1fr_auto] md:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-center px-4 py-2.5 text-sm" data-testid={`doublon-row-${c.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 truncate">{c.civilite ? `${c.civilite} ` : ''}{c.nom} {c.prenom}</p>
                    <p className="text-xs text-slate-400">Créé le {c.created_at ? new Date(c.created_at).toLocaleDateString('fr-FR') : '—'}{c.entreprise ? ` · ${c.entreprise}` : ''}</p>
                  </div>
                  <div className="hidden md:block min-w-0 text-xs text-slate-500 space-y-0.5">
                    <p className="flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{c.email || '—'}</p>
                    <p className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.telephone || '—'}</p>
                  </div>
                  <div className="hidden md:flex flex-wrap gap-1">
                    {affecte ? c.dossiers.map((code, i) => (
                      <span key={i} className="text-[10px] font-mono bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded">{code}</span>
                    )) : <span className="text-xs text-slate-300">Aucune AF</span>}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 disabled:text-slate-300"
                    disabled={affecte || deleting === c.id}
                    title={affecte ? `Affecté à ${c.dossiers.join(', ')}` : 'Supprimer cette fiche'}
                    onClick={() => handleDelete(c)}
                    data-testid={`doublon-delete-${c.id}`}
                  >
                    {deleting === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    <span className="ml-1.5 text-xs">{affecte ? `Affecté à ${c.dossiers.join(', ')}` : 'Supprimer'}</span>
                  </Button>
                </div>
              );
            })}
          </div>
        </Card>
      ))}

      <AlertDialog open={purgeOpen} onOpenChange={setPurgeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer {purgeables} doublon(s) ?</AlertDialogTitle>
            <AlertDialogDescription>
              Dans chaque groupe, les fiches affectées à une AF sont conservées ; sinon la fiche la plus ancienne est gardée. Les autres fiches non affectées seront supprimées définitivement.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handlePurge} disabled={purging} className="bg-red-600 hover:bg-red-700" data-testid="purge-doublons-confirm">
              {purging ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
